/**
 * The waiting posture, in one place, as `period.ts` is for periods.
 *
 * A live `[C]` block has been measured at 26 seconds and at 128 seconds, on the
 * same block with the same prompt (open item 39). Whatever the workbench shows
 * while a document assembles has to survive two minutes on one paragraph, and it
 * has to survive it without lying.
 *
 * **The Cowork ruling, and every line here is one clause of it:**
 *
 * *The deterministic blocks render instantly.* Statutory text, office standing
 * paragraphs, every substituted slot — they are on the page in the first second,
 * because none of them ever went near a model. Only the `[C]` paragraphs wait,
 * and they wait individually.
 *
 * *Elapsed time, never a percentage.* No true percentage exists: the server does
 * not know how long a block will take, and a bar that crept to 90% and sat there
 * would be inventing a number, which is rule 1 wearing a progress indicator.
 *
 * *The page is never locked.* Everything already on it can be read, selected,
 * scrolled and printed while the rest arrives.
 *
 * *A starved block becomes a standard amber chip.* Not a special error state —
 * the same chip as every other blank the officer fills, because that is what it
 * now is. A paragraph the model could not write and a field the case never
 * recorded are the same job of work.
 *
 * *Past sixty seconds, a quiet note.* Long enough that the ordinary case never
 * sees it; early enough that a two-minute wait is explained before the officer
 * starts wondering whether the tool has hung.
 */

/** The one place the note appears, and the one number that decides it. */
export const QUIET_NOTE_AFTER_MS = 60_000

/**
 * Roughly the poll interval. What the workbench asks the server on.
 *
 * **It must keep asking while the window is not in front** — the query sets
 * `refetchIntervalInBackground`. The library's default pauses an interval
 * whenever the browser reports the page hidden (another tab, a minimised
 * window, a window fully covered by another), while the seconds on the strip
 * are counted by a plain timer that does not pause. So a page that is not in
 * front shows *2 of 2 case paragraphs still drafting · 46s* over a draft the
 * worker finished in four seconds — the count running and nothing asking —
 * which is the picture walkthrough 32.34 recorded.
 */
export const POLL_INTERVAL_MS = 1_500

/**
 * What a block's state means on the page.
 *
 * `template` is everything a model never touched — most of the document. It is
 * deliberately indistinguishable from a finished `[C]` block: a reader of the
 * issued instrument is not shown which paragraphs a machine helped with, and the
 * provenance layer already says what each block is.
 */
export type BlockTone = 'settled' | 'waiting' | 'working' | 'blank'

export interface DraftInput {
  draft_state?: string | null
  draft_started_at?: string | null
  draft_ms?: number | null
  draft_attempt?: number | null
}

export interface DraftReading {
  tone: BlockTone
  /**
   * What the page says about a `[C]` block, or null where there is nothing to
   * say. While the block is awaited this stands **in place of** its text; on a
   * blank it is the strip above the scaffold.
   */
  label: string | null
  /** True while this block is still owed: its text is not drawn, the label is. */
  awaiting: boolean
}

/**
 * Whole seconds, never negative.
 *
 * `draft_started_at` is the **server's** clock and this runs on the browser's.
 * They differ by however far the desk's clock has drifted, so a block that
 * started a moment ago can read as starting in the future — and the prototype's
 * negative-zero bug is precisely what happens next: `-0` renders as `0`, `-0 < 0`
 * is false, and a strip that should say *just started* says nothing at all.
 * Clamped at zero, which is honest in both directions and cannot produce "-3s".
 */
export function elapsedSeconds(startedAt: string | null | undefined, now: number): number {
  if (!startedAt) return 0
  const began = Date.parse(startedAt)
  if (Number.isNaN(began)) return 0
  return Math.max(0, Math.floor((now - began) / 1000))
}

/** `9s` · `1m 04s`. Mono-friendly, and it never shows a bare zero as blank. */
export function readElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}m ${String(rest).padStart(2, '0')}s`
}

/**
 * What stands on the page where a paragraph has not arrived yet.
 *
 * It used to be the strip *Queued* over the template's own scaffold — `2.0
 * ⟦body_paras⟧`, `⟦proposal⟧` — which is the app's storage shown to an officer
 * as though it were the document (walkthrough 32.34). The paragraph is not
 * there yet, and that is all the page has to say about it.
 */
export const BEING_WRITTEN = 'Being written…'

/**
 * One `[C]` block, as the page should read it.
 *
 * - `pending` and `drafting` — not here yet. One sentence for both: since a
 *   document is drafted by one call, every paragraph reads `pending` for the
 *   whole of it, so *Queued* was said over paragraphs that were being written.
 *   The seconds are counted once, on the strip above the document, not on
 *   every paragraph. A second attempt is still said, in words — a wait that
 *   doubles for a stated reason is a different wait from one that doubles for
 *   none.
 * - `drafted` — settled, and nothing is said. Nothing marks a finished
 *   paragraph as machine-assisted on the document itself.
 * - `blank` — the scaffold stands and there is an amber chip for it. The strip
 *   says so rather than leaving a paragraph that looks unfinished for no stated
 *   reason.
 */
export function readDraft(block: DraftInput, reason?: string | null): DraftReading {
  const state = block.draft_state ?? 'template'
  if (state === 'template' || state === 'drafted') {
    return { tone: 'settled', label: null, awaiting: false }
  }
  if (state === 'blank') {
    // **Why it is blank, when the server said why.** One sentence stood for
    // every blank there is, and the two it has to tell apart are opposites:
    // *Sarvam is unreachable: timed out* means press Draft again, and *no
    // earlier noting is present in the case record* means this paragraph
    // should not be on this document at all. Shown the same words, an officer
    // rewrites by hand a paragraph that only needed retrying — or retries one
    // the model was right to decline.
    //
    // The reason is the server's own sentence, already recorded against the
    // block and already in the payload; it simply never reached the strip.
    // Nothing is composed here out of it, because a reason this screen
    // paraphrases is a reason that drifts from the one in the record.
    const said = (reason ?? '').trim()
    return {
      tone: 'blank',
      label: said
        ? `Not drafted — ${said}`
        : 'Not drafted — the template stands and this is yours to fill.',
      awaiting: false,
    }
  }
  if (state === 'pending') {
    return { tone: 'waiting', label: BEING_WRITTEN, awaiting: true }
  }

  const attempt = block.draft_attempt ?? 1
  return {
    tone: 'working',
    // Above one means the first request was retried, at a raised budget or
    // with the shape quoted back. Both roughly double the wait.
    label:
      attempt > 1
        ? `${BEING_WRITTEN} The first try did not come back; trying again.`
        : BEING_WRITTEN,
    awaiting: true,
  }
}

export interface AssemblyInput {
  drafting?: boolean | null
  generation_started_at?: string | null
  blocks?: DraftInput[]
  /** Set by the server ONLY where nothing is reading the queue. The client
   * cannot work this out — it can see that a document is slow, never that a
   * job is unattended. */
  service_unavailable?: string | null
}

export interface AssemblyReading {
  /** True while any paragraph is still owed. Drives the poll, nothing else. */
  drafting: boolean
  /** Waiting on a queue nobody is reading — not a slow document. The note
   * carries the sentence; this is what makes it amber rather than grey. */
  stalled: boolean
  awaiting: number
  /** `2 of 5 paragraphs still drafting`, or null when nothing is owed. */
  headline: string | null
  /**
   * The quiet note, past sixty seconds. Null before that — an ordinary
   * generation should never show it, and a message that appears every time is a
   * message nobody reads by the second week.
   */
  note: string | null
  elapsedSeconds: number
}

export function readAssembly(version: AssemblyInput, now: number): AssemblyReading {
  const blocks = version.blocks ?? []
  const awaiting = blocks.filter((block) => readDraft(block).awaiting).length
  const seconds = elapsedSeconds(version.generation_started_at, now)
  const drafting = Boolean(version.drafting)

  if (!drafting && awaiting === 0) {
    return {
      drafting: false,
      awaiting: 0,
      headline: null,
      note: null,
      elapsedSeconds: seconds,
      stalled: false,
    }
  }

  const total = blocks.filter(
    (block) => (block.draft_state ?? 'template') !== 'template',
  ).length

  return {
    drafting,
    awaiting,
    headline:
      awaiting === 0
        ? 'Finishing.'
        : `${awaiting} of ${total} case ${total === 1 ? 'paragraph' : 'paragraphs'} still drafting · ${readElapsed(seconds)}`,
    // **Two different waits, and the note is the only thing that tells them
    // apart** (walkthrough entry 27, fix 2). One is a paragraph being written,
    // which is worth waiting for. The other is a job nobody has picked up, and
    // no amount of waiting fixes it — the server says which, because only the
    // server can see whether anything is reading the queue.
    //
    // The reassuring note must not stand in front of the second: an officer
    // told "a case paragraph can take two minutes" waits two minutes, then ten,
    // and eventually downloads a document of ruled blanks. That is exactly what
    // happened on 30 Aug 2026.
    note: version.service_unavailable
      ? version.service_unavailable
      : seconds >= QUIET_NOTE_AFTER_MS / 1000
        ? 'A case paragraph can take two minutes. The rest of the document is finished and yours to read, ' +
          'and anything that does not arrive becomes a blank you fill.'
        : null,
    stalled: Boolean(version.service_unavailable),
    elapsedSeconds: seconds,
  }
}
